// Dashboard — session launcher + AI page wrapper.
// Shows the Preflight check-in (camera mirror + sensor pills + Start
// button) before the session starts, then renders <AI /> full-bleed
// once the user hits Start. The tool picker is gone — every tool is on
// inside a session now, so the picker was solving a problem that no
// longer exists.

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CssBaseline,
  ThemeProvider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AI from './AI';
import { Boards } from './enums';
import AppHeader from './components/AppHeader';
import Preflight from './Preflight';
import { theme, colors } from './theme';

// In-session view always gets the full toolkit. The old per-tool toggle
// is gone; this is just the list the <AI /> component reads to know
// which dashboards to mount.
const ALL_TOOLS: Boards[] = [Boards.computerVision, Boards.dragLine, Boards.rapha];

export const Dashboard = (_props: { theme?: any }) => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [search] = useSearchParams();
  // Wave 20.44 — /session/focus deep-links here with ?focus=<class>
  // (or 'new'). Null when the rider came in the old way and hasn't
  // picked a focus — the session runs class-agnostic in that case.
  const focusClass = search.get('focus');
  const [inSession, setInSession] = useState(false);
  // Wave 20.16 fix — the outer End Session button must run the same
  // persist-and-report flow as the fullscreen one. AI (and AIClientVision
  // below it) writes its End handler into this ref on mount.
  const endSessionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const token = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || '');
    if (!token) {
      window.location.href = '/login';
      return;
    }

    fetch(`${process.env.REACT_APP_API}/users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(() => {})
      .catch(() => (window.location.href = '/login'));
  }, []);

  if (inSession) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', backgroundColor: colors.bg }}>
          <AppHeader />
          <AI
            theme={theme}
            dashboards={ALL_TOOLS}
            focusClass={focusClass}
            // Wave 20.40 — after the report Done button, drop the
            // rider on the feed instead of back at the launcher.
            onEndSession={() => {
              setInSession(false);
              navigate('/feed');
            }}
            endSessionRef={endSessionRef}
          />
          <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
            <Button
              variant="outlined"
              onClick={() => endSessionRef.current?.()}
              sx={{
                backgroundColor: 'rgba(18, 24, 38, 0.85)',
                backdropFilter: 'blur(12px)',
              }}
            >
              {t('endSession')}
            </Button>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundColor: colors.bg }}>
        <AppHeader />
        <Preflight focusClass={focusClass} onStart={() => setInSession(true)} />
      </Box>
    </ThemeProvider>
  );
};
